import { Component, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DeviceService } from '@core/services/util/device.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { GtmService } from '@app/core/services/analytics/gtm.service';
import { FreebieService } from '@core/services/freebie/freebie.service';
import { UtilityService } from '@app/core/services/util/utility.service';
import { MenuUtilService } from '@app/core/services/util/menu-util.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';
import { LocalCart, CartService, Item } from '@core/services/cart/cart.service';
import { PreorderService, TimeDetails } from '@core/services/preorder/preorder.service';
import { CustomizationService } from '@core/services/customization/customization.service';
import { CustomizationUtilService } from '@core/services/customization-util/customization-util.service';
import { RupeePipe } from '@app/shared/pipes/rupee.pipe';
import { BrandProductFilterPipe } from '@shared/pipes/brand-product-filter.pipe';
import { PonchoUIModule } from '@poncho/ui';
import { CartViewItemErrorComponent } from '../cart-view-item-error/cart-view-item-error.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgTemplateOutlet, NgClass, KeyValuePipe } from '@angular/common';

import { InfoDialogComponent } from '@shared/components/info-dialog/info-dialog.component';
import { CustomizationComponent } from '@shared/components/customization/customization.component';

import { OutletCloseTimer } from './outlet-closer-timer';
import { OutletOpeningTiming } from './outlet-opening-timing';

import { Menu } from '@app/shared/models/api-response/outlet-timing-response';
import { BrandStatePipe } from '@app/shared/pipes/brand-state.pipe';
import { TypeofPipe } from '@app/shared/pipes/typeof.pipe';

type BrandDetail = { brand_id: number; brand_name: string; total_cost: number; quantity: number };

@Component({
    selector: 'app-multi-brand-cart-view',
    templateUrl: './multi-brand-cart-view.component.html',
    styleUrls: ['./multi-brand-cart-view.component.scss'],
    standalone: true,
    imports: [NgTemplateOutlet, MatExpansionModule, NgClass, CartViewItemErrorComponent, PonchoUIModule, BrandProductFilterPipe, RupeePipe, KeyValuePipe, BrandStatePipe, TypeofPipe]
})
export class MultiBrandCartViewComponent implements OnDestroy, OnChanges {
    @Input() isCartDropdown = false;
    @Input() preorder: {
        is_selected: false;
        time: null;
    };
    @Input() oultetClosingStrip = false;

    @Output() updateLoader = new EventEmitter<boolean>();

    // local cart
    cart: LocalCart;
    cart_items: LocalCart['items'];
    cartHasPass: Item | null;

    // server cart
    serverCartData = [];
    unavailableItems = {};

    // sampling
    samplingItems = [];
    samplingProducts = [];
    samplingProductsIds: Set<number> = new Set();

    // freeitem
    freeItems: Item[] = [];

    // servicable brand info
    brandState = {
        normal: new Set<number>(),
        preorder: new Set<number>(),
        closed: new Set<number>()
    };
    brandName = {};
    outlet_closing_timer = {};
    outlet_opening_timing = {};
    appState: string;
    timeDetails: TimeDetails;

    isMobile = false;
    loading = true;
    isApiInProgress = false;
    outletData: any;
    total_quantity: number;
    subscriptionSavings = 0;
    brand_details: BrandDetail[] = [];
    destroySubject$: Subject<void> = new Subject();

    constructor(
        private dialog: MatDialog,
        private gtmService: GtmService,
        private cartService: CartService,
        private menuUtil: MenuUtilService,
        private deviceService: DeviceService,
        private outletService: OutletService,
        private utilityService: UtilityService,
        private freebieService: FreebieService,
        private preorderService: PreorderService,
        private localStorage: LocalStorageService,
        private customizationService: CustomizationService,
        private customizationUtil: CustomizationUtilService
    ) {
        this.isMobile = this.deviceService.isMobile();
    }

    ngOnInit() {
        this.freebieService.state.pipe(takeUntil(this.destroySubject$)).subscribe(sampling => {
            if (sampling.length) {
                this.samplingProducts = sampling;
                this.samplingProductsIds = this.freebieService.getSamplingProductSizeIds();
                this.initializeBrandDetails();
                this.hydrateSampling();
            }
        });

        this.cartService.state.pipe(takeUntil(this.destroySubject$)).subscribe((cart: LocalCart) => {
            if (!this.isCartDropdown) {
                const isEmptyCart = Boolean(!Object.keys(cart.items).length);
                if (!isEmptyCart) {
                    this.updateLoader.emit(true);
                }
            }

            this.cart = JSON.parse(JSON.stringify(cart));
            this.cart_items = Object.values(cart.items);
            this.total_quantity = this.cart_items.reduce((acc, item) => acc + item.quantity, 0);

            this.freeItems = [];
            this.serverCartData = this.cart_items.map(item => ({
                ...item,
                marked_price: item.marked_price * item.quantity,
                price: item.price * item.quantity
            }));

            this.cartHasPass = this.cart_items.find(item => item.is_subscription_item) || null;

            if (this.isCartDropdown) {
                this.initializeBrandDetails();
                this.initaliseBrandStates(this.appState, this.timeDetails);
            }
                
            this.hydrateSampling();
        });

        this.outletService.state.pipe(takeUntil(this.destroySubject$)).subscribe(data => {
            if (data?.outlet) {
                this.outletData = data.outlet;
            }
        });

        this.cartService.serverCart.pipe(takeUntil(this.destroySubject$)).subscribe(data => {
            
            if (this.isCartDropdown) {
                return;
            }

            try {
                // TODO: proper handling
                if (data?.items) {
                    this.updateCartDetails(data.items);
                }
                
                this.subscriptionSavings = data.subscription_saving;
                
                const { cart_change_error } = data;
                
                this.unavailableItems = {};
                cart_change_error?.unavailable_items.forEach((item) => {
                    this.unavailableItems[item.id] = item;
                })

                this.freeItems = data.items
                    .filter((item: Item) => item.type === 'FreeItem')
                    .map((item: Item) => ({
                        ...item,
                        ...this.getFreeItemDetails(item.id)
                    }));
                this.isApiInProgress = false;
            } catch (e) {
                this.gtmService.logEvent('js_errors', {
                    event_label: 'server_cart_error',
                    event_value: e
                });
            }
        });

        this.preorderService.servicableBrandMetaData.pipe(takeUntil(this.destroySubject$)).subscribe(data => {
            if (data) {
                const {
                    timeDetails,
                    primaryBrandData: { state }
                } = data;

                if (!timeDetails || this.isCartDropdown) {
                    return;
                }

                this.appState = state;
                this.timeDetails = timeDetails;
                this.resetBrandState();
                this.initaliseBrandStates(state, timeDetails);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // user selects/ removes preorder
        const preorderSelectionToggled = changes.preorder?.previousValue?.is_selected !== changes.preorder?.currentValue?.is_selected;
        if (preorderSelectionToggled) {
            this.resetBrandState();
            this.initaliseBrandStates(this.appState, this.timeDetails);
            this.gtmService.logWebAnalyticsEvent("cart_page", "User Toggled Preorder");
        }

        // don't show individual brand closing timer incase main app closing timer strip is being shown
        const isPrimaryBrandClosingTimerShown = changes.oultetClosingStrip?.currentValue?.isClosing;
        if (isPrimaryBrandClosingTimerShown) {
            this.removeIndividualBrandTimer();
        }
    }

    removeIndividualBrandTimer() {
        if (!this.cart) {
            return;
        }

        const items = Array.from(Object.values(this.cart.items));
        const itemBrandIds = items.map(data => data.brand_id);

        itemBrandIds.forEach((id: number) => {
            this.outlet_closing_timer[id]?.timerSub$?.unsubscribe();
            this.outlet_closing_timer[id] = null;
        });
    }

    initaliseBrandStates(appState = '', timeDetails: TimeDetails = null) {
        if (!this.cart) {
            return;
        }

        this.loading = true;
        const items = Array.from(Object.values(this.cart.items));
        const itemBrandIds = items.map(data => data.brand_id);

        itemBrandIds.forEach((id: number) => {
            if (this.isCartDropdown) {
                this.brandState.normal.add(id);
                return;
            }

            const brandMenu = timeDetails?.menus.find(menu => menu.brand_id === id);
            if (!brandMenu) {
                return;
            }

            this.setupOutletTimers(brandMenu, id, timeDetails);

            if (this.preorder.is_selected) {
                this.brandState.normal.add(id);
                return;
            }

            const brandState = this.utilityService.getBrandState(brandMenu);
            switch (appState) {
                case 'active':
                    switch (brandState) {
                        case 'active':
                            this.brandState.normal.add(id);
                            break;
                        case 'preorder':
                            this.brandState.preorder.add(id);
                            break;
                        case 'closed':
                            this.brandState.closed.add(id);
                            break;
                    }
                    break;

                case 'preorder':
                    if (brandState === 'active' || brandState === 'preorder') {
                        this.brandState.normal.add(id);
                    } else {
                        this.brandState.closed.add(id);
                    }
                    break;

                default:
                    this.brandState.normal.add(id);
                    break;
            }
        });

        this.loading = false;
    }

    setupOutletTimers(brandMenu: Menu, id: number, timeDetails: TimeDetails) {
        this.outlet_closing_timer[id]?.timerSub$?.unsubscribe();
        this.outlet_closing_timer[id] = null;

        this.outlet_closing_timer[id] = new OutletCloseTimer(
            brandMenu.outlet_timings,
            timeDetails.currentServerTime,
            this.onClosed.bind(this, id)
        );

        this.outlet_opening_timing[id] = new OutletOpeningTiming(brandMenu.outlet_timings);
    }

    onClosed(brand_id: number) {
        this.gtmService.logWebAnalyticsEvent("cart_page", "Brand State Moved From Open To Closed With Id " + brand_id);

        // to avoid multiple api call: 2-3 brands closing at same time
        if (!this.isApiInProgress) {
            this.isApiInProgress = true;
            const distinctBrandsIds = this.cartService.getBrandIds();
            this.preorderService.getPreorderSlots(distinctBrandsIds);
        }
    }

    removeInActiveBrands() {
        const { closed, normal, preorder } = this.brandState;
        const inActiveBrands = [...preorder, ...closed];
        const brands = inActiveBrands.map((brand_id: number) => this.brandName[brand_id]).join(', ');

        let message = !normal.size
            ? 'Oops! All items in your cart are currently unavailable.'
            : `Oops! Items from ${brands} are currently not available.`;

        if (preorder.size) {
            const preorderBrands = [...preorder].map((brand_id: number) => this.brandName[brand_id]).join(', ');
            message += `\n \n You can select another delivery slot for ordering from ${preorderBrands}.`;
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = 350;
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.data = {
            header: 'Items Unavailable',
            message,
            buttonText: !normal.size ? 'Clear Cart' : 'Update Cart'
        };

        this.gtmService.logWebAnalyticsEvent("cart_page", "User Clicks To Remove Inactive Brands");

        this.dialog
            .open(InfoDialogComponent, dialogConfig)
            .afterClosed()
            .subscribe(data => {
                if (data === 'proceed') {
                    this.removeAllItems(['closed', 'preorder']);
                    this.gtmService.logWebAnalyticsEvent("cart_page", "User Proceeds To Remove Inactive Brands");
                }
            });
    }

    updateCartDetails(items: Item[]) {
        this.serverCartData.forEach(item => {
            const index = items.find(i => item.id === i.id);

            if (index) {
                item.price = Math.round(index.discounted_cost);
                item.marked_price = index.cost;
                item.quantity = index.quantity;
                item.brand_name = index.brand_name;
                this.brandName[item.brand_id] = item.brand_name;
                if (typeof (item?.description) === 'object') {
                    if (Object.keys(index.combo_bogo_remarks).length) {
                        for (const id in item.description) {
                            item.description[id]['remarks'] = index.combo_bogo_remarks[id]
                                ? index.combo_bogo_remarks[id].remarks
                                : '';
                        }
                    } else {
                        for (const id in item.description) {
                            item.description[id]['remarks'] = '';
                        }
                    }
                }
            }
        });

        this.samplingItems.forEach(item => {
            const index = items.find(i => item.id === i.id);

            if (index) {
                item.price = Math.round(index.discounted_cost);
                item.marked_price = index.cost;
                item.brand_name = index.brand_name;
            }
        })

        this.initializeBrandDetails();
        this.updateLoader.emit(false);
    }

    initializeBrandDetails() {
        const details = this.serverCartData
            .filter(item => !item.is_subscription_item && !this.samplingProductsIds?.has(item.ids.product_size_id))
            .reduce((acc, currentValue) => {
                const { brand_id, brand_name } = currentValue;

                if (!acc[brand_id]) {
                    acc[brand_id] = {
                        brand_id,
                        brand_name
                    };
                }
                return acc;
            }, {});

        this.brand_details = Array.from(Object.values(details));
    }

    customize(item: Item) {
        const localItem = this.cart_items.filter((data: Item) => data.id === item.id)[0];

        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = 'customization-dialog';
        dialogConfig.data = {
            ...localItem.ids,
            quantity: localItem.quantity,
            remark: localItem.remark,
            isCustomize: true,
            menuData: this.cart.itemDetails[localItem.ids.menu_id]
        };
        this.dialog.open(CustomizationComponent, dialogConfig);
    }

    updateCart(operation: Event, item: Item) {
        const action = operation.type;

        const localItem = this.cart_items.filter(data => data.id === item.id)[0];

        switch (action) {
            case 'add':
                const menu = this.cart.itemDetails[localItem.ids.menu_id];
                if (!this.cartService.checkMaxLimit(localItem.ids.product_id, menu)) {
                    return;
                }
                if (this.customizationService.toShowCustomizationModal(localItem.ids.product_id, menu)) {
                    this.customizationUtil.repeatCustomize(localItem, menu);
                } else {
                    this.cartService.incrementQuantity(localItem);
                }
                break;

            case 'remove':
                this.cartService.decrementQuantity(localItem);
                break;

            default:
                break;
        }
    }

    getFinalCartPrice(): number {
        return this.cartService.getFinalCartPrice();
    }

    removePass(item: Item) {
        if (this.subscriptionSavings) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.maxWidth = 400;
            dialogConfig.width = '90vw';
            dialogConfig.data = {
                header: 'Are you sure?',
                message: `You'll have to pay ₹ ${this.subscriptionSavings} extra on this order. Don't miss out on EatClub benefits.`,
                buttonText: 'REMOVE'
            };
            this.dialog
                .open(InfoDialogComponent, dialogConfig)
                .afterClosed()
                .subscribe(data => {
                    if (data === 'close' || !data) {
                        return;
                    }
                    this.cartService.decrementQuantity(item);
                });
        } else {
            this.cartService.decrementQuantity(item);
        }
    }

    openPassDialog() {
        if (!this.isMobile) {
            this.menuUtil.openProductPassesModal();
        } else {
            this.menuUtil.openProductPassesModal(['pass-cart-page'], 'cart');
        }
    }

    samplingProductLabel(id: number) {
        try {
            const localSamplingCart = this.localStorage.get('freebie_cart');
            return localSamplingCart[id];
        } catch (err) {
            this.gtmService.logEvent('js_errors', {
                event_label: 'product_label_error',
                event_value: err
            });
        }
    }

    hydrateSampling() {
        const localSamplingCart = this.localStorage.get('freebie_cart');
        if (localSamplingCart) {
            this.samplingItems = this.samplingProducts.filter(
                (item: any) => localSamplingCart[item.ids.product_size_id] !== undefined
            );
        }
    }

    toggleSamplingItem(sampling, label: string) {
        this.freebieService.toggleSamplingItem(sampling, label);
        this.hydrateSampling();
    }

    getFreeItemDetails(id: string) {
        const productSizeId = id.split('-')[2];
        const productId = this.outletData.product_sizes.filter((item: any) => item.id == productSizeId)[0].product_id;
        const product = this.outletData.products.filter((item: any) => item.id == productId)[0];
        return {
            label: product.label,
            description: product.description
        };
    }

    removeAllItems(states: string[]) {
        const brands = [];
        states.forEach(state => {
            brands.push(...this.brandState[state]);
        });

        const items = this.cart_items.filter(item => {
            return brands.includes(item.brand_id);
        });

        this.cartService.decrementMultipleItems(items);
    }

    resetBrandState() {
        this.brandState.closed = new Set();
        this.brandState.normal = new Set();
        this.brandState.preorder = new Set();
    }

    unsorted(_a: any, _b: any): number {
        return 0;
    }

    reviewItem(type: string, item: Item) {
        if (type === "customization") {
            this.customize(item);
        } else {
            const localItem = this.cart_items.filter(data => data.id === item.id);
            this.cartService.decrementMultipleItems(localItem);
        }
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();

        Object.values(this.outlet_closing_timer).forEach((timer: any) => {
            timer?.timerSub$?.unsubscribe();
        });
    }
}